import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { api, formatApiError, setToken, getToken } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import { ADMIN_IDS } from "@/constants/testIds/site";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (getToken()) nav("/admin", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email: email.trim().toLowerCase(), password });
      setToken(data.token);
      toast.success(`Welcome, ${data.user.name}`);
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="hidden lg:flex relative items-center justify-center bg-[#0B1220] text-white p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #0057B8 0, transparent 45%), radial-gradient(circle at 80% 70%, #0E7490 0, transparent 45%)" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="FASTag CSP" className="w-12 h-12 rounded-full object-contain bg-white/10 ring-1 ring-white/20" />
            <div>
              <div className="font-display font-bold text-lg">FASTag CSP</div>
              <div className="text-xs uppercase tracking-widest text-[#F59E0B]">Sayeed Anwar · Bally · Admin</div>
            </div>
          </div>
          <h1 className="mt-10 font-display text-4xl font-bold leading-tight max-w-md">Manage orders & enquiries in one place.</h1>
          <p className="mt-4 text-white/75 max-w-md">View and update the status of FASTag orders and contact messages from the website.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl border border-[#E2E8F0] p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#0057B8]/10 text-[#0057B8] grid place-items-center"><Lock className="w-5 h-5" /></div>
            <div>
              <h2 className="font-display text-2xl font-bold text-[#111827]">Admin sign-in</h2>
              <p className="text-sm text-[#6B7280]">Restricted area — authorised staff only.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="admin-email">Email</Label>
              <Input id="admin-email" data-testid={ADMIN_IDS.loginEmail} value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="admin@fastagcsp.com" className="focus-visible:ring-[#0057B8]" />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input id="admin-password" data-testid={ADMIN_IDS.loginPassword} value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="••••••••" className="focus-visible:ring-[#0057B8]" />
            </div>
            <Button type="submit" disabled={busy} data-testid={ADMIN_IDS.loginSubmit} className="w-full bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full h-11 btn-brand active:scale-95">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
            </Button>
            <div className="text-center text-xs text-[#6B7280]">
              <Link to="/" className="hover:text-[#0057B8]">← Back to website</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
